import { useState, useRef, useEffect } from 'react';
import { MapPin, LocateFixed, Info, RotateCcw, Mic, Check, ArrowLeft, Plus, Minus, Trash2, Package, ShoppingCart } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, X, Sparkles, Loader2 } from 'lucide-react';
import { addMessage, setLoading, setError, clearError, clearChat } from '../store/chatSlice';
import { safeFetch } from '../App';
import { useVoiceRecognition } from '../hooks/useVoiceRecognition';
import ReactMarkdown from 'react-markdown';

const API_BASE_URL = 'https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default';

// Add custom animation styles
const styleTag = document.createElement('style');
styleTag.textContent = `
  @keyframes gradient-shift {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
  }

  @keyframes sparkle-pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.7; transform: scale(0.95); }
  }
`;
if (!document.querySelector('[data-ai-chat-styles]')) {
  styleTag.setAttribute('data-ai-chat-styles', 'true');
  document.head.appendChild(styleTag);
}

export default function AIChatBar() {
  const [isOpen, setIsOpen] = useState(false);
  // Hide/show state for chat button
  const [isChatVisible, setIsChatVisible] = useState(() => {
    const stored = localStorage.getItem('ai_chat_visible');
    return stored === null ? true : stored === 'true';
  });

  // Persist isChatVisible to localStorage
  useEffect(() => {
    localStorage.setItem('ai_chat_visible', isChatVisible);
  }, [isChatVisible]);

  // Mode selection state
  const [mode, setMode] = useState(() => localStorage.getItem('ai_mode') || null);
  // True when the global StoreAdmin is mounted — flips the chat into admin
  // mode (admin starter prompts, skip the community zone/radius picker).
  const [isAdminMode, setIsAdminMode] = useState(
    () => typeof window !== 'undefined' && !!window.__mekomit_admin_active
  );
  // True when the customer is inside a storefront (StoreView sets the flag) —
  // flips the chat into order-taking mode: skip the zone/radius picker and
  // match free-text requests against the store catalog.
  const [isStoreMode, setIsStoreMode] = useState(
    () => typeof window !== 'undefined' && !!window.__mekomit_store_active
  );
  // The AI-interpreted order awaiting the customer's approval / edits.
  // { items: [{ product_id, name, price, image, quantity, paid_options:[{name,price}] }], unmatched: [], needs_clarification }
  const [pendingOrder, setPendingOrder] = useState(null);
  const [selectedZones, setSelectedZones] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('ai_zones')) || [];
    } catch { return []; }
  });
  const [radius, setRadius] = useState(() => Number(localStorage.getItem('ai_radius')) || 3);
  const [modeConfirmed, setModeConfirmed] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [userLocation, setUserLocation] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const dispatch = useDispatch();
  const { messages, isLoading, error } = useSelector(state => state.chat);
  const [zones, setZones] = useState([]);
  const chatId = localStorage.getItem('chat_id');

  // Voice recognition
  const {
    isRecording,
    transcript,
    isSupported: isVoiceSupported,
    error: voiceError,
    startRecording,
    stopRecording,
  } = useVoiceRecognition();

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Each time the chat opens, re-sync admin mode. If we're in admin context,
  // skip the zones/radius picker — those questions don't apply to admin help.
  useEffect(() => {
    if (!isOpen) return;
    const adminNow = typeof window !== 'undefined' && !!window.__mekomit_admin_active;
    const storeNow = typeof window !== 'undefined' && !adminNow && !!window.__mekomit_store_active;
    setIsAdminMode(adminNow);
    setIsStoreMode(storeNow);
    if (adminNow && modeConfirmed !== 'ready') {
      setMode('distance');
      setModeConfirmed('ready');
    } else if (storeNow && modeConfirmed !== 'ready') {
      // Customer in a store — go straight to the order chat, no picker.
      setMode('store');
      setModeConfirmed('ready');
    }
  }, [isOpen, modeConfirmed]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Mirror the live transcript so the stop handler can read the final text.
  const previousTranscriptRef = useRef('');
  const liveTranscriptRef = useRef('');

  // Mirror the live transcript into the input box as the user speaks.
  useEffect(() => {
    if (transcript !== previousTranscriptRef.current) {
      previousTranscriptRef.current = transcript;
      liveTranscriptRef.current = transcript;
      if (transcript) setInputValue(transcript);
    }
  }, [transcript]);

  // Mic button: start listening, or — when already recording — stop and submit
  // whatever was dictated. Recording never auto-stops; the user decides when
  // they're done by clicking the button.
  const handleVoiceButtonClick = () => {
    if (isRecording) {
      stopRecording();
      // Give the engine a moment to flush the final words, then submit.
      setTimeout(() => {
        const text = (liveTranscriptRef.current || '').trim();
        if (text) {
          setInputValue('');
          sendQuery(text);
        }
      }, 450);
    } else {
      previousTranscriptRef.current = '';
      liveTranscriptRef.current = '';
      setInputValue('');
      startRecording();
    }
  };

  // Fetch zones on mount
  useEffect(() => {
    const fetchZones = async () => {
      try {
        const res = await safeFetch(
          `${API_BASE_URL}/zones-and-categories`,
          chatId
        );
        const data = await res.json();
        console.log('Fetched zones data:', data.zones); // Debug log
        setZones(data.zones || []);
      } catch (err) {
        console.error('Failed to fetch zones:', err);
      }
    };

    if (chatId) {
      fetchZones();
    }
  }, [chatId]);

  // Auto-seed the chat's selected zones from the user's zones — the same
  // list shown in "המקומיות שלי" (UserZones). Source of truth is the API.
  // We only seed when nothing is already chosen, so we never overwrite an
  // intentional pick the user made in this or a previous chat session.
  useEffect(() => {
    if (!chatId) return;
    if (selectedZones && selectedZones.length > 0) return;

    let cancelled = false;
    const seed = async () => {
      try {
        const res = await safeFetch(
          `${API_BASE_URL}/user-locals?chat_id=${encodeURIComponent(chatId)}`,
          chatId
        );
        if (!res?.ok) return;
        const data = await res.json();
        const zoneIds = Array.isArray(data?.user_locals) ? data.user_locals.filter(Boolean) : [];
        if (cancelled || zoneIds.length === 0) return;
        setSelectedZones(zoneIds);
        try { localStorage.setItem('ai_zones', JSON.stringify(zoneIds)); } catch { /* ignore */ }
      } catch (err) {
        console.warn('Failed to auto-seed user zones for AI chat:', err);
      }
    };

    seed();
    return () => { cancelled = true; };
    // Only react to chatId — running once per chat session is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId]);

  // Get user location on mount or when mode changes to 'distance'
  useEffect(() => {
    if (mode === 'distance') {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setUserLocation({
              lat: position.coords.latitude,
              lon: position.coords.longitude,
            });
          },
          (error) => {
            setMode('zones');
            setShowTooltip(true);
          }
        );
      }
    }
  }, [mode]);

  const handleResetSession = () => {
    setPendingOrder(null);
    // In store mode keep the order chat ready — just clear the conversation.
    if (isStoreMode) {
      dispatch(clearChat());
      return;
    }
    setMode(null);
    setSelectedZones([]);
    setRadius(3);
    setModeConfirmed(false);
    localStorage.removeItem('ai_mode');
    localStorage.removeItem('ai_zones');
    localStorage.removeItem('ai_radius');
    dispatch(clearChat());
  };

  // --- Store order-taking -------------------------------------------------
  // Sends the customer's free-text request + the store catalog to the order
  // matcher and stashes the interpreted order for approval. Used by both the
  // text form and the voice auto-submit path.
  const submitStoreOrder = async (question, history) => {
    const ctx = (typeof window !== 'undefined' && window.__mekomit_store_context) || {};
    setPendingOrder(null);
    dispatch(addMessage({ role: 'user', content: question, timestamp: new Date().toISOString() }));
    dispatch(setLoading(true));
    dispatch(clearError());
    try {
      const catalog = (ctx.products || []).map(p => ({
        product_id: p.product_id,
        name: p.name,
        description: p.description,
        price: p.price,
        category: p.category,
        paid_options: p.paid_options || [],
      }));
      const payload = { mode: 'store_order', message: question, catalog, store_name: ctx.store_name || '', history };
      const response = await safeFetch(
        `${API_BASE_URL}/ai-query`,
        chatId,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) },
      );
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const data = await response.json();
      const order = data.order || {};
      const items = (order.items || []).map(it => {
        const prod = (ctx.products || []).find(p => p.product_id === it.product_id);
        if (!prod) return null;
        return {
          product_id: it.product_id,
          name: prod.name,
          price: prod.price,
          image: prod.image,
          quantity: Math.max(1, parseInt(it.quantity, 10) || 1),
          paid_options: it.paid_options || [],
        };
      }).filter(Boolean);
      dispatch(addMessage({ role: 'assistant', content: order.reply || 'הנה ההזמנה שהבנתי:', timestamp: new Date().toISOString() }));
      setPendingOrder({ items, unmatched: order.unmatched || [], needs_clarification: !!order.needs_clarification });
    } catch (err) {
      console.error('Store order error:', err);
      dispatch(setError('שגיאה בעיבוד ההזמנה. נסו שוב.'));
      dispatch(addMessage({ role: 'assistant', content: 'מצטער, אירעה שגיאה. נסו שוב.', timestamp: new Date().toISOString() }));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const orderItemUnit = (it) =>
    (parseFloat(it.price) || 0) + (it.paid_options || []).reduce((s, o) => s + (parseFloat(o.price) || 0), 0);
  const orderTotal = pendingOrder
    ? pendingOrder.items.reduce((s, it) => s + orderItemUnit(it) * it.quantity, 0)
    : 0;

  const updateOrderQty = (idx, delta) => {
    setPendingOrder(prev => {
      if (!prev) return prev;
      const items = prev.items
        .map((it, i) => (i === idx ? { ...it, quantity: it.quantity + delta } : it))
        .filter(it => it.quantity > 0);
      return { ...prev, items };
    });
  };
  const removeOrderItem = (idx) =>
    setPendingOrder(prev => prev ? { ...prev, items: prev.items.filter((_, i) => i !== idx) } : prev);

  const approveOrder = () => {
    if (!pendingOrder?.items?.length) return;
    try {
      window.dispatchEvent(new CustomEvent('mekomit:store-add-to-cart', {
        detail: {
          items: pendingOrder.items.map(it => ({
            product_id: it.product_id,
            quantity: it.quantity,
            paid_options: it.paid_options,
          })),
        },
      }));
    } catch { /* ignore CustomEvent ctor failure on ancient browsers */ }
    dispatch(addMessage({ role: 'assistant', content: 'מעולה! הוספתי את הפריטים לסל 🛒', timestamp: new Date().toISOString() }));
    setPendingOrder(null);
    setIsOpen(false);
  };

  // Unified send path — used by the text form and the voice stop handler.
  const sendQuery = async (question) => {
    if (!question || isLoading) return;
    // Build history from previous messages (excluding the current input)
    const history = messages.map(msg => ({ role: msg.role, content: msg.content }));
    // Storefront → order-taking path.
    if (typeof window !== 'undefined' && window.__mekomit_store_active && window.__mekomit_store_context) {
      return submitStoreOrder(question, history);
    }
    dispatch(addMessage({ role: 'user', content: question, timestamp: new Date().toISOString() }));
    dispatch(setLoading(true));
    dispatch(clearError());
    try {
      let payload = { question, mode, user_location: userLocation || { lat: 32.0853, lon: 34.7818 }, history };
      if (mode === 'zones') {
        payload.zones = selectedZones.length > 0 ? selectedZones : ['zikhron'];
      } else if (mode === 'distance') {
        payload.radius_km = radius;
      }
      if (isAdminMode) {
        payload.admin = true;
        if (typeof window !== 'undefined' && window.__mekomit_admin_context) {
          payload.store_context = window.__mekomit_admin_context;
        }
      }
      // Save last-used mode/params
      localStorage.setItem('ai_mode', mode);
      localStorage.setItem('ai_zones', JSON.stringify(selectedZones));
      localStorage.setItem('ai_radius', String(radius));
      const response = await safeFetch(
        `${API_BASE_URL}/ai-query`,
        chatId,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const data = await response.json();
      dispatch(addMessage({ role: 'assistant', content: data.answer || 'מצטער, לא הצלחתי למצוא תשובה.', timestamp: new Date().toISOString() }));
    } catch (err) {
      console.error('AI Chat error:', err);
      dispatch(setError('שגיאה בשליחת השאלה. נסה שוב.'));
      dispatch(addMessage({ role: 'assistant', content: 'מצטער, אירעה שגיאה. אנא נסה שוב.', timestamp: new Date().toISOString() }));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const question = inputValue.trim();
    if (!question || isLoading) return;
    setInputValue('');
    sendQuery(question);
  };

  // Mode selector UI
  const renderModeSelector = () => (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 24 }}
      transition={{ duration: 0.35 }}
      className="flex flex-col items-center justify-center p-6 gap-6 bg-white min-h-[240px] sm:min-h-[320px]"
    >
      <div className="flex flex-col items-center gap-2 mb-2">
        <span className="text-lg font-bold text-ink-strong">איך תרצה שאחפש עבורך?</span>
        <span className="text-sm text-ink-subtle flex items-center gap-1">
          <Info className="w-4 h-4 text-brand-400" />
          בחר האם לחפש לפי קהילות או לפי מרחק ממך
        </span>
      </div>
      <div className="flex gap-4 w-full justify-center">
        <motion.button
          whileHover={{ scale: 1.06 }}
          whileTap={{ scale: 0.97 }}
          className="flex flex-col items-center gap-2 px-6 py-5 rounded-2xl shadow-md transition-all duration-200 bg-gradient-to-br from-brand-400 to-brand-600 text-white font-bold text-lg w-40"
          onClick={() => {
            setMode('zones');
            setShowTooltip(false);
            setModeConfirmed(true);
          }}
        >
          <MapPin className="w-8 h-8 mb-1" />
          לפי קהילות
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.06 }}
          whileTap={{ scale: 0.97 }}
          className="flex flex-col items-center gap-2 px-6 py-5 rounded-2xl shadow-md transition-all duration-200 bg-gradient-to-br from-brand-500 to-brand-400 text-white font-bold text-lg w-40"
          onClick={() => {
            setMode('distance');
            setModeConfirmed(true);
          }}
        >
          <LocateFixed className="w-8 h-8 mb-1" />
          לפי מרחק
        </motion.button>
      </div>
      {showTooltip && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-4 text-xs text-warning-700 bg-warning-50 border border-warning-100 rounded-xl px-3 py-2 shadow">
          לא ניתן היה לקבל מיקום, עברתי לחיפוש לפי קהילות.
        </motion.div>
      )}
    </motion.div>
  );

  // Zone multi-select UI
  const renderZoneSelector = () => {
    // Handle both string arrays and object arrays
    const getZoneId = (zone) => typeof zone === 'string' ? zone : (zone.zone_id || zone.id);
    const getZoneName = (zone) => typeof zone === 'string' ? zone : (zone.name || zone.zone_id || zone.id);

    return (
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 24 }}
        transition={{ duration: 0.35 }}
        className="flex flex-col items-center justify-center p-4 gap-3 bg-white min-h-[240px] sm:min-h-[320px]"
      >
        <span className="text-base font-bold text-ink-strong">בחר קהילות</span>
        <div className="flex flex-wrap gap-1.5 justify-center max-h-48 overflow-y-auto w-full px-2">
          {zones.length === 0 ? (
            <span className="text-ink-subtle text-sm">טוען אזורים...</span>
          ) : (
            zones.map(zone => {
              const zoneId = getZoneId(zone);
              const zoneName = getZoneName(zone);
              const isSelected = selectedZones.includes(zoneId);

              return (
                <button
                  key={zoneId}
                  className={`px-2 py-1 rounded-lg text-xs border transition-all ${isSelected
                    ? 'border-brand-400 bg-brand-400 text-white font-semibold'
                    : 'border-brand-400 bg-white text-ink hover:bg-surface-muted'
                    }`}
                  onClick={() => {
                    setSelectedZones(prev =>
                      prev.includes(zoneId)
                        ? prev.filter(z => z !== zoneId)
                        : [...prev, zoneId]
                    );
                  }}
                >
                  {zoneName}
                </button>
              );
            })
          )}
        </div>
        <div className="flex gap-2 mt-2">
          <button
            className="px-4 py-1.5 rounded-lg bg-surface-sunken text-ink font-semibold text-sm shadow hover:bg-line transition-all"
            onClick={() => { setModeConfirmed(false); }}
          >
            חזור
          </button>
          <button
            className="px-5 py-1.5 rounded-lg bg-brand-400 text-white font-semibold text-sm shadow hover:bg-brand-500 transition-all disabled:opacity-50"
            onClick={() => selectedZones.length > 0 && setModeConfirmed('ready')}
            disabled={selectedZones.length === 0}
          >
            המשך
          </button>
        </div>
      </motion.div>
    );
  };

  // Distance/radius selector UI
  const renderRadiusSelector = () => (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 24 }}
      transition={{ duration: 0.35 }}
      className="flex flex-col items-center justify-center p-6 gap-6 bg-white min-h-[240px] sm:min-h-[320px]"
    >
      <span className="text-lg font-bold text-ink-strong mb-2">בחר רדיוס חיפוש (ק"מ)</span>
      <input
        type="range"
        min={1}
        max={10}
        step={1}
        value={radius}
        onChange={e => setRadius(Number(e.target.value))}
        className="w-64 accent-brand-400"
      />
      <div className="flex items-center gap-2 text-base font-bold">
        <LocateFixed className="w-5 h-5 text-brand-400" />
        <span>{radius} ק"מ</span>
      </div>
      <div className="flex gap-3 mt-4">
        <button
          className="px-5 py-2 rounded-xl bg-surface-sunken text-ink font-bold text-base shadow hover:bg-line transition-all"
          onClick={() => { setModeConfirmed(false); }}
        >
          חזור
        </button>
        <button
          className="px-6 py-2 rounded-xl bg-brand-400 text-white font-bold text-base shadow hover:bg-brand-500 transition-all"
          onClick={() => setModeConfirmed('ready')}
        >
          המשך
        </button>
      </div>
    </motion.div>
  );

  return (
    <>
      {/* Floating Chat Button + Hide/Show Tab.
       * Wrapper is pointer-events-none so the fixed z-[10001] box never
       * swallows taps on page content behind it (e.g. admin action buttons);
       * each interactive control below re-enables pointer events on itself. */}
      <div
        className="fixed z-[10001] flex items-center pointer-events-none"
        style={{
          right: '1rem',
          bottom: 'calc(env(safe-area-inset-bottom, 0px) + 92px)',
          // Responsive right offset
        }}
      >
        {/* Chat Button (slides in/out) */}
        <motion.button
          onClick={() => {
            if (!isChatVisible) setIsChatVisible(true);
            setIsOpen(v => !v);
          }}
          className={`relative text-white rounded-full p-4 shadow-xl shadow-brand-500/50 hover:shadow-2xl hover:shadow-brand-500/60 transition-all duration-200 focus:outline-none ${isChatVisible ? 'pointer-events-auto' : 'pointer-events-none opacity-0'}`}
          style={{
            background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 50%, #8B5CF6 100%)',
            backgroundSize: '200% 200%',
            animation: 'gradient-shift 3s ease infinite',
            transform: isChatVisible ? 'translateX(0)' : 'translateX(80px)',
            transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1), opacity 0.3s',
            boxShadow: '0 8px 32px 0 rgba(139,92,246,0.25)',
          }}
          whileHover={isChatVisible ? { scale: 1.1 } : {}}
          whileTap={isChatVisible ? { scale: 0.95 } : {}}
          aria-label="פתח צ'אט AI"
        >
          {/* Magical sparkle effect */}
          <div className="absolute -top-1 -right-1 w-3 h-3 bg-warning-100 rounded-full animate-ping" />
          <div className="absolute -top-1 -right-1 w-3 h-3 bg-warning-100 rounded-full" />

          <AnimatePresence mode="wait">
            {isOpen ? (
              <motion.div
                key="close"
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <X className="w-6 h-6" />
              </motion.div>
            ) : (
              <motion.div
                key="open"
                initial={{ rotate: 90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: -90, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="relative"
              >
                <Sparkles className="w-6 h-6" style={{ animation: 'sparkle-pulse 2s ease-in-out infinite' }} />
              </motion.div>
            )}
          </AnimatePresence>
          {/* Hide tab (chevron or X) attached to the button, only when visible */}
          {isChatVisible && (
            <button
              type="button"
              onClick={e => { e.stopPropagation(); setIsChatVisible(false); }}
              className="absolute -right-4 top-1/2 -translate-y-1/2 bg-white text-brand-600 rounded-full shadow-md border border-brand-200 w-7 h-7 flex items-center justify-center transition-all duration-300 hover:bg-brand-50 hover:text-brand-800 focus:outline-none"
              style={{
                boxShadow: '0 2px 8px 0 rgba(139,92,246,0.10)',
                zIndex: 1,
              }}
              aria-label="הסתר צ'אט"
              tabIndex={0}
            >
              {/* Show chat icon (arrow) */}
              <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5l7 7-7 7" /></svg>
            </button>
          )}
        </motion.button>
        {/* Show tab (peeking out) when hidden */}
        {!isChatVisible && (
          <button
            type="button"
            onClick={() => setIsChatVisible(true)}
            className="ml-2 bg-white text-brand-600 rounded-full shadow-md border border-brand-200 w-10 h-10 flex items-center justify-center transition-all duration-300 hover:bg-brand-50 hover:text-brand-800 focus:outline-none pointer-events-auto"
            style={{
              transform: 'translateX(0)',
              boxShadow: '0 2px 8px 0 rgba(139,92,246,0.10)',
              zIndex: 2,
              position: 'relative',
              right: '-12px', // peeking out from edge
            }}
            aria-label="הצג צ'אט"
            tabIndex={0}
          >
            {/* Hide chat icon (chevron or X) */}
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          </button>
        )}
      </div>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed z-[10001] w-96 max-w-[calc(100vw-1rem)] sm:max-w-[calc(100vw-3rem)] bg-white rounded-2xl shadow-2xl overflow-hidden border border-line right-2 sm:right-6 flex flex-col"
            style={{
              bottom: 'calc(env(safe-area-inset-bottom, 0px) + 184px)',
              // Cap to viewport so the panel can't overflow off-screen on
              // short phones. 200px leaves room for the floating button
              // below + a comfortable margin above the safe-area top.
              maxHeight: 'calc(100dvh - env(safe-area-inset-bottom, 0px) - env(safe-area-inset-top, 0px) - 200px)',
              pointerEvents: 'auto',
              opacity: 1,
            }}
          >
            {/* Header */}
            <div
              className="text-white p-4 flex items-center gap-3"
              style={{
                background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 50%, #8B5CF6 100%)',
                backgroundSize: '200% 200%',
                animation: 'gradient-shift 3s ease infinite',
              }}
            >
              <Sparkles className="w-5 h-5 animate-pulse" />
              <div className="flex-1">
                <h3 className="font-semibold text-lg text-white"> ✨ עוזר המקומית ✨</h3>
                <p className="text-xs text-brand-100">
                  {isStoreMode ? 'הזמנה מהירה — ספרו לי מה תרצו' : isAdminMode ? 'עוזר ניהול לחנות שלך' : 'שאל.י אותי כל דבר על הקהילה שלך'}
                </p>
              </div>
              {(mode || modeConfirmed) && (
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={handleResetSession}
                  className="p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-all"
                  title="אפס הגדרות"
                >
                  <RotateCcw className="w-4 h-4" />
                </motion.button>
              )}
            </div>
            {/* Mode/param selection flow */}
            {!modeConfirmed && renderModeSelector()}
            {modeConfirmed && mode === 'zones' && modeConfirmed !== 'ready' && renderZoneSelector()}
            {modeConfirmed && mode === 'distance' && modeConfirmed !== 'ready' && renderRadiusSelector()}
            {/* Chat UI only after mode/params confirmed */}
            {modeConfirmed === 'ready' && <>
              {/* Selection Indicator */}
              <div className="bg-gradient-to-r from-brand-50 to-secondary-50 border-b border-brand-200 px-4 py-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {isStoreMode ? (
                      <>
                        <Sparkles className="w-4 h-4 text-brand-600 flex-shrink-0" />
                        <span className="text-xs font-semibold text-brand-700">הזמנה חכמה מהחנות</span>
                      </>
                    ) : mode === 'zones' ? (
                      <>
                        <MapPin className="w-4 h-4 text-brand-600 flex-shrink-0" />
                        <div className="flex flex-col gap-1 min-w-0">
                          <span className="text-xs font-semibold text-brand-700">אזורים נבחרים:</span>
                          <div className="flex flex-wrap gap-1">
                            {selectedZones.length > 0 ? (
                              selectedZones.map(zoneId => {
                                const zone = zones.find(z =>
                                  (typeof z === 'string' ? z : (z.zone_id || z.id)) === zoneId
                                );
                                const zoneName = zone
                                  ? (typeof zone === 'string' ? zone : (zone.name || zone.zone_id || zone.id))
                                  : zoneId;
                                return (
                                  <span
                                    key={zoneId}
                                    className="inline-block px-2 py-0.5 bg-brand-100 text-brand-800 rounded-full text-xs font-medium border border-brand-200"
                                  >
                                    {zoneName}
                                  </span>
                                );
                              })
                            ) : (
                              <span className="text-xs text-brand-600 italic">כל האזורים</span>
                            )}
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <LocateFixed className="w-4 h-4 text-brand-600 flex-shrink-0" />
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-brand-700">רדיוס חיפוש:</span>
                          <span className="inline-block px-2 py-0.5 bg-brand-100 text-brand-800 rounded-full text-xs font-medium border border-brand-200">
                            {radius} ק"מ
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex-1 min-h-0 max-h-96 overflow-y-auto p-4 space-y-3 bg-surface-muted">
                {messages.length === 0 && (
                  isStoreMode ? (
                    <div className="text-center text-ink-subtle py-8">
                      <div className="relative inline-block">
                        <Sparkles className="w-12 h-12 mx-auto mb-3 text-brand-400" style={{ animation: 'sparkle-pulse 2s ease-in-out infinite' }} />
                        <div className="absolute top-0 left-0 w-12 h-12 bg-brand-400/20 rounded-full animate-ping" />
                      </div>
                      <p className="text-sm font-semibold text-brand-600">מה תרצו להזמין? ✨</p>
                      <p className="text-sm mt-1">ספרו לי בכתב או בדיבור,</p>
                      <p className="text-sm">ואני אתאים את זה למוצרים בחנות</p>
                      <p className="text-xs mt-3 text-ink-faint">לדוגמה: "שתי כיכרות לחם וחלב"</p>
                    </div>
                  ) : isAdminMode ? (
                    <AdminStarterPrompts onPick={(text) => { setInputValue(text); inputRef.current?.focus(); }} />
                  ) : (
                    <div className="text-center text-ink-subtle py-8">
                      <div className="relative inline-block">
                        <Sparkles className="w-12 h-12 mx-auto mb-3 text-brand-400" style={{ animation: 'sparkle-pulse 2s ease-in-out infinite' }} />
                        <div className="absolute top-0 left-0 w-12 h-12 bg-brand-400/20 rounded-full animate-ping" />
                      </div>
                      <p className="text-sm font-semibold text-brand-600">התחל שיחה קהילתית! ✨</p>
                      <p className="text-sm mt-1">שאל אותי על עסקים, מפגשים,</p>
                      <p className="text-sm">או כל דבר אחר באזור שלך</p>
                    </div>
                  )
                )}
                {messages.map((msg, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${msg.role === 'user'
                        ? 'bg-gradient-to-br from-brand-500 to-secondary-500 text-white shadow-md shadow-brand-500/30'
                        : 'bg-gradient-to-br from-brand-50 to-secondary-50 text-ink-strong border border-brand-200'
                        }`}
                    >
                      <div className="text-sm whitespace-pre-wrap prose prose-sm max-w-none">
                        {parseGoMarkers(msg.content).map((part, partIdx) => (
                          part.type === 'go' ? (
                            <GoButton
                              key={`go-${partIdx}`}
                              tab={part.tab}
                              action={part.action}
                              onNavigate={() => setIsOpen(false)}
                            />
                          ) : (
                            <ReactMarkdown
                              key={`text-${partIdx}`}
                              components={{
                                a: ({ node, href, children, ...props }) => {
                                  const handleClick = (e) => {
                                    e.preventDefault();
                                    if (window.Telegram?.WebApp?.openLink) {
                                      window.Telegram.WebApp.openLink(href);
                                    } else {
                                      window.open(href, '_blank', 'noopener,noreferrer');
                                    }
                                  };
                                  return (
                                    <a href={href} onClick={handleClick} className="text-brand-600 hover:text-brand-800 underline font-semibold cursor-pointer inline-flex items-center gap-1" {...props}>
                                      {children}
                                    </a>
                                  );
                                },
                                p: ({ node, ...props }) => <p {...props} className="inline" />,
                              }}
                            >
                              {part.value}
                            </ReactMarkdown>
                          )
                        ))}
                      </div>
                      <p className="text-xs mt-1 opacity-60">
                        {new Date(msg.timestamp).toLocaleTimeString('he-IL', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </motion.div>
                ))}
                {/* Loading Indicator */}
                {isLoading && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex justify-start"
                  >
                    <div className="bg-gradient-to-r from-brand-50 to-secondary-50 text-ink-strong border border-brand-200 rounded-2xl px-4 py-3 flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-brand-500" />
                      <Sparkles className="w-4 h-4 text-secondary-500 animate-pulse" />
                      <span className="text-sm font-medium">מחפש..</span>
                    </div>
                  </motion.div>
                )}
                {/* Error Message */}
                {error && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="bg-danger-50 border border-danger-100 text-danger-700 rounded-lg px-3 py-2 text-sm"
                  >
                    {error}
                  </motion.div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Proposed order — approve / edit before adding to cart */}
              {isStoreMode && pendingOrder && (
                <div className="border-t border-brand-200 bg-surface-muted px-4 py-3 max-h-72 overflow-y-auto">
                  {pendingOrder.items.length > 0 ? (
                    <>
                      <div className="space-y-2">
                        {pendingOrder.items.map((it, idx) => (
                          <div key={`${it.product_id}_${idx}`} className="flex items-center gap-2 bg-white border border-brand-100 rounded-xl p-2">
                            {it.image ? (
                              <img src={it.image} alt={it.name} className="w-11 h-11 rounded-lg object-cover flex-shrink-0" loading="lazy" />
                            ) : (
                              <div className="w-11 h-11 rounded-lg bg-brand-50 flex items-center justify-center flex-shrink-0">
                                <Package className="w-5 h-5 text-brand-300" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <h4 className="text-sm font-semibold text-ink-strong truncate" title={it.name}>{it.name}</h4>
                              <p className="text-xs text-ink-subtle">₪{orderItemUnit(it).toFixed(2)} ליחידה</p>
                              {it.paid_options?.length > 0 && (
                                <p className="text-2xs text-brand-600 truncate">{it.paid_options.map(o => o.name).join(' · ')}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <button onClick={() => updateOrderQty(idx, -1)} aria-label="הפחת" className="w-6 h-6 rounded-md bg-brand-100 hover:bg-brand-200 text-brand-800 flex items-center justify-center">
                                <Minus className="w-3.5 h-3.5" />
                              </button>
                              <span className="w-5 text-center text-sm font-bold text-ink-strong">{it.quantity}</span>
                              <button onClick={() => updateOrderQty(idx, +1)} aria-label="הוסף" className="w-6 h-6 rounded-md bg-brand-500 hover:bg-brand-600 text-white flex items-center justify-center">
                                <Plus className="w-3.5 h-3.5" />
                              </button>
                              <button onClick={() => removeOrderItem(idx)} aria-label="הסר" className="w-6 h-6 rounded-md bg-danger-50 hover:bg-danger-100 text-danger-700 flex items-center justify-center">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>

                      {pendingOrder.unmatched?.length > 0 && (
                        <div className="mt-2 text-xs text-warning-700 bg-warning-50 border border-warning-100 rounded-lg px-3 py-2">
                          לא נמצאה התאמה עבור: {pendingOrder.unmatched.join(', ')}
                        </div>
                      )}

                      <div className="flex items-center justify-between mt-2 px-1 text-sm font-bold text-ink-strong">
                        <span>סה"כ משוער:</span>
                        <span>₪{orderTotal.toFixed(2)}</span>
                      </div>

                      <button
                        onClick={approveOrder}
                        className="mt-2 w-full flex items-center justify-center gap-2 bg-gradient-to-r from-brand-500 to-secondary-500 hover:from-brand-600 hover:to-secondary-600 text-white font-bold py-2.5 rounded-xl transition-all shadow"
                      >
                        <ShoppingCart className="w-4 h-4" />
                        אישור והוספה לסל
                      </button>
                    </>
                  ) : (
                    <p className="text-sm text-ink-subtle text-center py-2">
                      לא מצאתי מוצרים תואמים. נסו לנסח אחרת.
                    </p>
                  )}
                </div>
              )}

              {/* Input Form */}
              <form onSubmit={handleSubmit} className="p-4 bg-white border-t border-line">
                {/* Voice error message */}
                {voiceError && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-2 text-xs text-warning-700 bg-warning-50 border border-warning-100 rounded-lg px-3 py-2"
                  >
                    {voiceError}
                  </motion.div>
                )}
                <div className="flex gap-2 items-center">
                  <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder={isRecording ? "מקשיב..." : (isStoreMode ? "מה תרצו להזמין?" : isAdminMode ? "שאל כל דבר על ניהול החנות..." : "שאל כל דבר על האזור שלך...")}
                    className="flex-1 px-4 py-2.5 border border-line-strong rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent text-sm"
                    disabled={isLoading || isRecording}
                    dir="auto"
                  />
                  {/* Voice Input Button */}
                  {isVoiceSupported && (
                    <motion.button
                      type="button"
                      onClick={handleVoiceButtonClick}
                      disabled={isLoading}
                      className={`p-2.5 rounded-xl transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed ${isRecording
                        ? 'bg-success-500 text-white shadow-success-500/50 animate-pulse'
                        : 'bg-gradient-to-br from-brand-500 to-secondary-500 text-white shadow-brand-500/50 hover:scale-110'
                        }`}
                      whileHover={!isRecording && !isLoading ? { scale: 1.1 } : {}}
                      whileTap={!isLoading ? { scale: 0.95 } : {}}
                      aria-label={isRecording ? "סיים הקלטה ושלח" : "התחל הקלטה"}
                    >
                      {isRecording ? (
                        <Check className="w-5 h-5" />
                      ) : (
                        <Mic className="w-5 h-5" />
                      )}
                    </motion.button>
                  )}
                  <button
                    type="submit"
                    disabled={!inputValue.trim() || isLoading}
                    className="text-white p-2.5 rounded-xl hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-brand-500/50 disabled:shadow-none"
                    style={{
                      background: !inputValue.trim() || isLoading
                        ? '#9CA3AF'
                        : 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                    }}
                    aria-label="שלח הודעה"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </form>
            </>}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

/**
 * Admin AI panel. Shown inside the chat empty state when the user is
 * inside the store admin (window.__mekomit_admin_active flag, set by
 * StoreAdmin). Acts as a complete admin-AI hub: capabilities, grouped
 * starter prompts, and a closing hint. Clicking a prompt drops the text
 * into the input so the owner can edit before sending.
 */
const ADMIN_PROMPT_GROUPS = [
  {
    title: 'תוכן שיווקי',
    hint: 'כתיבה, תרגום, סלוגנים',
    prompts: [
      'תכתוב לי תיאור שיווקי קצר למוצר: [שם המוצר]',
      'תכתוב סלוגן קצר וקליל לחנות שלי',
      'תרגם את שם החנות והתיאור שלי לאנגלית',
    ],
  },
  {
    title: 'קשר עם לקוחות',
    hint: 'הודעות, קופונים, שאלות נפוצות',
    prompts: [
      'תכתוב הודעה חמה שתופיע ללקוח אחרי שהוא מסיים הזמנה',
      'תציע לי רעיון לקופון שיביא לקוחות חדשים',
      'תכתוב מדיניות ביטולים ברורה וקצרה בעברית',
    ],
  },
  {
    title: 'תפעול וארגון',
    hint: 'קטגוריות, מוצרים, סדר',
    prompts: [
      'תציע 5 שמות לקטגוריות שמתאימות לחנות שלי',
      'איך כדאי לי לארגן את המוצרים שלי לפי סדר עדיפות?',
      'תכתוב לי 3 שאלות נפוצות שמתאימות לחנות שלי',
    ],
  },
];

/**
 * Parse a chat message for [GO:tab=<id>,action=<intent>] markers. Returns
 * a list of alternating text / go parts so the renderer can interleave
 * Markdown text with clickable navigation buttons.
 *
 * Format accepted:
 *   [GO:tab=coupons]
 *   [GO:tab=coupons,action=create]
 *   [GO:tab=store-details]
 *   [GO: tab = coupons , action = create ]   (whitespace tolerated)
 */
const GO_MARKER_RE = /\[GO:\s*tab\s*=\s*([a-z][a-z0-9-]*)(?:\s*,\s*action\s*=\s*([a-z][a-z0-9-]*))?\s*\]/gi;

function parseGoMarkers(content) {
  if (!content || typeof content !== 'string') return [{ type: 'text', value: content || '' }];
  if (!content.includes('[GO:') && !content.includes('[go:')) return [{ type: 'text', value: content }];

  const parts = [];
  let lastIndex = 0;
  let m;
  GO_MARKER_RE.lastIndex = 0;
  while ((m = GO_MARKER_RE.exec(content)) !== null) {
    if (m.index > lastIndex) {
      parts.push({ type: 'text', value: content.slice(lastIndex, m.index) });
    }
    parts.push({ type: 'go', tab: m[1], action: m[2] || null });
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < content.length) {
    parts.push({ type: 'text', value: content.slice(lastIndex) });
  }
  return parts;
}

/**
 * Inline button rendered for each [GO:...] marker. Clicking dispatches a
 * window event that StoreAdmin listens to — it switches tab and (if the
 * marker carried action=create) signals the tab to open its create modal.
 */
const TAB_LABELS = {
  dashboard:       'לוח בקרה',
  analytics:       'ניתוח נתונים',
  'store-details': 'הגדרות חנות',
  products:        'מוצרים',
  orders:          'הזמנות',
  categories:      'קטגוריות',
  coupons:         'קופונים',
  delivery:        'חלוקה',
  timeslots:       'משבצות זמן',
  settings:        'תשלומים',
};

function GoButton({ tab, action, onNavigate }) {
  const label = TAB_LABELS[tab] || tab;
  const isCreate = action === 'create';
  const handleClick = () => {
    try {
      window.dispatchEvent(new CustomEvent('mekomit:admin-navigate', {
        detail: { tab, action: action || null },
      }));
    } catch {
      // ignore CustomEvent ctor failure on truly ancient browsers
    }
    onNavigate?.();
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      className="my-1.5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-pill bg-brand-500 hover:bg-brand-600 active:bg-brand-700 text-white text-xs font-semibold shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
    >
      {isCreate ? <Plus className="w-3.5 h-3.5" aria-hidden="true" /> : <ArrowLeft className="w-3.5 h-3.5" aria-hidden="true" />}
      <span>{isCreate ? `יצירה ב${label}` : `מעבר ל${label}`}</span>
    </button>
  );
}

function AdminStarterPrompts({ onPick }) {
  return (
    <div className="py-2">
      <div className="text-center pb-4 mb-3 border-b border-line-subtle">
        <div className="relative inline-block">
          <Sparkles className="w-9 h-9 mx-auto mb-2 text-secondary-500" style={{ animation: 'sparkle-pulse 2s ease-in-out infinite' }} />
        </div>
        <p className="text-sm font-bold text-brand-700">עוזר הניהול של מקומית</p>
        <p className="text-xs text-ink-muted mt-1 leading-relaxed px-4">
          כתיבת תכנים, רעיונות לקופונים, ארגון של החנות וכל מה שיכול לחסוך לך זמן.
        </p>
      </div>

      <div className="space-y-4">
        {ADMIN_PROMPT_GROUPS.map((group, gi) => (
          <div key={gi}>
            <div className="flex items-baseline justify-between mb-1.5 px-1">
              <span className="text-xs font-bold text-ink-strong">{group.title}</span>
              <span className="text-2xs text-ink-faint">{group.hint}</span>
            </div>
            <div className="space-y-1.5">
              {group.prompts.map((p, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => onPick?.(p)}
                  className="w-full text-start px-3 py-2 rounded-lg bg-white border border-line hover:border-brand-300 hover:bg-brand-50/60 transition-colors text-xs text-ink-strong leading-relaxed focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <p className="text-2xs text-ink-faint text-center mt-4 leading-relaxed px-4">
        לחיצה תמלא את הטקסט בשורת ההודעה — אפשר לערוך לפני שליחה.
      </p>
    </div>
  );
}